/**
 * Preço e estoque do canal: a tabela que a Berytus mantém para cada produto ativo — de onde vem o preço,
 * margem, estoque para o canal, o anúncio atual, o buy box e os alertas.
 */
import { useState } from 'react';
import { Link } from 'react-router';
import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import * as Dialog from '@radix-ui/react-dialog';
import { ExternalLink, Pencil, RefreshCw, Search, Settings, X } from 'lucide-react';
import { evaluatePrice, type ChannelCosts } from '@berytus/pricing';
import type { ChannelSettingsResponse, OfferPage, OfferRow, PriceAlert, PriceSource } from '@berytus/contracts';
import { api, brl, int } from '../api.ts';
import { ErrorBox, Loading, Tag, buyBoxTag } from '../components/ui.tsx';
import { useMe } from '../components/AuthGate.tsx';
import { titleOf } from './Catalog.tsx';
import { Head, Pager, useChannelTab, useMeliAccount, useSearchBox, useUrlState } from './MeliChannel.tsx';
import { can } from '@berytus/contracts';

export const ALERT: Record<PriceAlert, { label: string; tone: string }> = {
  below_cost: { label: 'Abaixo do custo', tone: 'err' },
  above_pmc: { label: 'Acima do PMC', tone: 'err' },
  low_margin: { label: 'Margem baixa', tone: 'warn' },
  no_cost: { label: 'Sem custo', tone: 'warn' },
  no_price: { label: 'Sem preço', tone: 'err' },
  capped_at_pmc: { label: 'No teto do PMC', tone: 'info' },
  default_price: { label: 'Preço padrão', tone: 'off' },
};

const SOURCE: Record<PriceSource, { label: string; tone: string }> = {
  manual: { label: 'Na mão', tone: 'info' },
  buybox: { label: 'Buy box', tone: 'info' },
  engine: { label: 'Calculado', tone: 'ok' },
  api: { label: 'API', tone: 'ok' },
  default: { label: 'Padrão', tone: 'warn' },
  kit: { label: 'Kit (base × un.)', tone: 'ok' },
  none: { label: 'Sem preço', tone: 'err' },
};

const FROM: Record<string, string> = { product: 'produto', category: 'categoria', general: 'geral' };

const pct = (v: number | string | null | undefined) => (v == null ? '—' : `${Number(v).toLocaleString('pt-BR', { maximumFractionDigits: 1 })}%`);

const COLS: [string, number, string?][] = [
  ['Produto', 0], ['Custo / PMC', 112, 'num'], ['Regra', 110], ['Preço final', 170], ['Margem', 88, 'num'],
  ['Estoque', 96, 'num'], ['No anúncio', 170], ['Buy box', 190], ['Alertas', 180],
];

export function Prices() {
  const { account, isPending, error } = useMeliAccount();
  const ch = useChannelTab();
  const [params, set] = useUrlState();
  const [search, setSearch] = useSearchBox(params, set);
  const admin = can(useMe().tenant.access, 'prices', 'write');
  const qc = useQueryClient();
  const [editing, setEditing] = useState<OfferRow | null>(null);

  const page = Number(params.get('page') ?? 1);
  const query = new URLSearchParams();
  for (const k of ['q', 'category', 'alert', 'source', 'listed']) if (params.get(k)) query.set(k, params.get(k)!);
  query.set('page', String(page));
  query.set('pageSize', '50');
  const list = useQuery({
    queryKey: ['meli', 'offers', account?.id, query.toString()],
    queryFn: () => api<OfferPage>(`/channels/accounts/${account!.id}/offers?${query}`),
    enabled: !!account,
    placeholderData: keepPreviousData,
  });
  const settings = useQuery({
    queryKey: ['meli', 'settings', account?.id],
    queryFn: () => api<ChannelSettingsResponse>(`/channels/accounts/${account!.id}/settings`),
    enabled: !!account,
  });
  const st = settings.data?.settings;
  const costs: ChannelCosts | null = st
    ? { ...st, takeRatePct: st.defaultListingType === 'gold_pro' && st.takeRatePremiumPct != null ? st.takeRatePremiumPct : st.takeRatePct }
    : null;

  const refresh = () => qc.invalidateQueries({ queryKey: ['meli'] });
  const recompute = useMutation({ mutationFn: () => api(`/channels/accounts/${account!.id}/recompute`, { method: 'POST' }), onSuccess: refresh });
  const applyBuyBox = useMutation({ mutationFn: (listingId: string) => api(`/channels/listings/${listingId}/apply-buybox`, { method: 'POST' }), onSuccess: refresh });

  const sum = list.data?.summary;
  const rows = list.data?.items ?? [];
  const alert = params.get('alert');

  return (
    <>
      <Head title="Preço e estoque">
        <p>
          A tabela que a Berytus mantém para {ch.code === 'shopee' ? 'a' : 'o'} {ch.name}: de onde vem o preço de cada produto, a margem depois das taxas,
          o estoque que vai para o canal e o que está no anúncio hoje. O envio ao canal se liga em Configurações do canal → Envio ao canal.
        </p>
      </Head>
      {isPending ? <Loading /> : error ? <ErrorBox error={error} /> : !account ? (
        <div className="panel empty"><b>Nenhuma conta conectada</b>Conecte {ch.code === 'shopee' ? 'a' : 'o'} {ch.name} em Integração de canais.</div>
      ) : (
        <div className="stack-y">
          {settings.data && (
            <div className="notice small" style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
              <span>
                {settings.data.settings.pricingMode === 'engine' ? 'A Berytus calcula' : 'Preço vem pela API'} · {ch.code !== 'mercadolivre' ? '' : settings.data.settings.defaultListingType === 'gold_pro' ? 'Premium, ' : 'Clássico, '}comissão {pct(settings.data.settings.defaultListingType === 'gold_pro' && settings.data.settings.takeRatePremiumPct != null ? settings.data.settings.takeRatePremiumPct : settings.data.settings.takeRatePct)}
                {settings.data.settings.extraPct > 0 && <> + {pct(settings.data.settings.extraPct)} de outras taxas</>} · margem geral {pct(settings.data.settings.defaultMarginPct)}
                {settings.data.rules.length > 0 && <> · {settings.data.rules.length} regra(s) de margem</>}
                {settings.data.settings.capAtPmc && ' · teto no PMC'}
              </span>
              <span style={{ flex: 1 }} />
              <Link className="btn sm" to={`${ch.base}/configuracoes`}><Settings size={14} /> Configurar</Link>
              <button className="btn sm quiet" type="button" disabled={recompute.isPending} onClick={() => recompute.mutate()}><RefreshCw size={14} /> Recalcular</button>
            </div>
          )}

          {sum && (
            <div className="kpis">
              {(['below_cost', 'above_pmc', 'low_margin', 'no_cost'] as const).map((k) => (
                <button key={k} type="button" className={`kpi${alert === k ? ' active' : ''}`} style={{ textAlign: 'left', cursor: 'pointer', font: 'inherit' }}
                  onClick={() => set({ alert: alert === k ? null : k, page: null })} aria-pressed={alert === k}>
                  <span className="k-label">{ALERT[k].label}</span>
                  <span className="k-value" style={(sum.alerts[k] ?? 0) > 0 && ALERT[k].tone === 'err' ? { color: 'var(--err)' } : undefined}>{int(sum.alerts[k] ?? 0)}</span>
                  <span className="k-note">{k === 'low_margin' && settings.data ? `abaixo de ${pct(settings.data.settings.minMarginAlertPct)}` : k === 'no_cost' ? 'não dá para calcular' : 'produtos'}</span>
                </button>
              ))}
              <div className="kpi">
                <span className="k-label">Com preço</span>
                <span className="k-value">{int(sum.withPrice)}</span>
                <span className="k-note">de {int(sum.products)} ativos{sum.alerts.default_price ? ` · ${int(sum.alerts.default_price)} no padrão` : ''}</span>
              </div>
            </div>
          )}

          <div>
            <div className="toolbar">
              <label className="search">
                <Search size={16} />
                <span className="sr-only">Buscar</span>
                <input className="input" type="search" placeholder="Buscar por SKU, EAN ou nome" value={search} onChange={(e) => setSearch(e.target.value)} />
              </label>
              <select className="select" aria-label="Origem do preço" value={params.get('source') ?? ''} onChange={(e) => set({ source: e.target.value || null, page: null })}>
                <option value="">Qualquer origem</option>
                {(Object.keys(SOURCE) as PriceSource[]).map((k) => <option key={k} value={k}>{SOURCE[k].label}</option>)}
              </select>
              <select className="select" aria-label="Alertas" value={alert ?? ''} onChange={(e) => set({ alert: e.target.value || null, page: null })}>
                <option value="">Qualquer alerta</option>
                <option value="any">Com algum alerta</option>
                {(Object.keys(ALERT) as PriceAlert[]).map((k) => <option key={k} value={k}>{ALERT[k].label}</option>)}
              </select>
              <div className="seg" role="group" aria-label="Anúncio">
                {([['all', 'Todos'], ['with', 'Com anúncio'], ['without', 'Sem anúncio']] as const).map(([v, l]) => (
                  <button key={v} type="button" aria-pressed={(params.get('listed') ?? 'all') === v} onClick={() => set({ listed: v === 'all' ? null : v, page: null })}>{l}</button>
                ))}
              </div>
            </div>
            {(applyBuyBox.error || recompute.error) && <div style={{ marginBottom: 12 }}><ErrorBox error={applyBuyBox.error ?? recompute.error} /></div>}

            <div className="panel flush">
              {list.error ? <div style={{ padding: 18 }}><ErrorBox error={list.error} /></div>
                : !list.isPending && rows.length === 0 ? <div className="empty"><b>Nenhum produto encontrado</b>Mude a busca ou os filtros.</div>
                : (
                  <>
                    <div className="grid-wrap" style={{ opacity: list.isPlaceholderData ? 0.55 : 1 }}>
                      <table className="grid" style={{ width: '100%', minWidth: COLS.reduce((n, c) => n + (c[1] || 240), 0) }}>
                        <colgroup>{COLS.map(([, w], i) => <col key={i} style={w ? { width: w } : undefined} />)}</colgroup>
                        <thead><tr>{COLS.map(([l, , c], i) => <th key={i} className={c}>{l}</th>)}</tr></thead>
                        <tbody>
                          {list.isPending
                            ? Array.from({ length: 10 }, (_, i) => <tr key={i}>{COLS.map((_, j) => <td key={j}><div className="skeleton" /></td>)}</tr>)
                            : rows.map((o) => (
                                <tr key={o.productId} style={{ cursor: 'default' }}>
                                  <td>
                                    <div className="cell-2">
                                      <Link to={`/catalogo/${o.productId}`} title={o.title ?? undefined}>{titleOf(o)}</Link>
                                      <span className="sub">SKU {o.sku}{o.category && <> · {o.category}</>}{o.isKit && ' · kit'}</span>
                                    </div>
                                  </td>
                                  <td className="num">
                                    <div className="cell-2" style={{ alignItems: 'flex-end' }}>
                                      <span>{brl(o.cost)}</span>
                                      <span className="sub">PMC {brl(o.pmc)}</span>
                                    </div>
                                  </td>
                                  <td>{o.marginRule ? <>{pct(o.marginRule.pct)} <span className="small muted">{FROM[o.marginRule.from]}</span></> : '—'}</td>
                                  <td>
                                    <div className="cell-2">
                                      <span style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                                        <b className="tnum">{brl(o.finalPrice)}</b>
                                        {admin && <button className="icon-btn" style={{ width: 24, height: 24 }} type="button" aria-label="Definir preço na mão" onClick={() => setEditing(o)}><Pencil size={13} /></button>}
                                      </span>
                                      <span className="sub">
                                        {o.priceSource && <Tag tone={SOURCE[o.priceSource].tone}>{SOURCE[o.priceSource].label}</Tag>}
                                        {o.computedPrice && o.priceSource !== 'engine' && <> calculado {brl(o.computedPrice)}</>}
                                      </span>
                                    </div>
                                  </td>
                                  <td className="num" style={o.marginPct != null && settings.data && Number(o.marginPct) < settings.data.settings.minMarginAlertPct ? { color: 'var(--err)' } : undefined}>{pct(o.marginPct)}</td>
                                  <td className="num">
                                    <div className="cell-2" style={{ alignItems: 'flex-end' }}>
                                      <span>{o.finalStock != null ? int(o.finalStock) : '—'}</span>
                                      <span className="sub" title={o.blockedBy ?? undefined}>{o.blockedBy ? <span style={{ color: 'var(--err)' }}>bloqueado</span> : <>{int(o.available)} disp.{o.stockOverride != null && ' · ajuste'}</>}</span>
                                    </div>
                                  </td>
                                  <td>
                                    {o.listing ? (
                                      <div className="cell-2">
                                        <span>
                                          {brl(o.listing.price)}
                                          {o.listing.price && o.finalPrice && Math.abs(Number(o.listing.price) - Number(o.finalPrice)) > 0.009 && <span className="small" style={{ color: 'var(--warn)' }}> ≠ tabela</span>}
                                        </span>
                                        <span className="sub">
                                          {o.listing.permalink ? <a className="ext" href={o.listing.permalink} target="_blank" rel="noreferrer">{o.listing.externalId} <ExternalLink size={11} /></a> : o.listing.externalId}
                                          {o.listing.availableQuantity != null && <> · {int(o.listing.availableQuantity)} un.</>}
                                        </span>
                                      </div>
                                    ) : <span className="small muted">Sem anúncio</span>}
                                  </td>
                                  <td>
                                    {o.listing?.buyBoxStatus ? (
                                      <div className="cell-2">
                                        <span>{buyBoxTag(o.listing.buyBoxStatus)} {o.listing.buyBoxPrice && <b className="tnum">{brl(o.listing.buyBoxPrice)}</b>}</span>
                                        {o.listing.buyBoxPrice && (
                                          <span className="sub" style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                                            margem {pct(o.listing.buyBoxMarginPct)}
                                            {admin && o.listing.buyBoxStatus !== 'winning' && (
                                              <button className="btn sm" style={{ height: 22, padding: '0 8px', fontSize: 12 }} type="button" disabled={applyBuyBox.isPending}
                                                onClick={() => applyBuyBox.mutate(o.listing!.id)} title="Usa o preço para ganhar como preço na mão deste produto">
                                                Aplicar
                                              </button>
                                            )}
                                          </span>
                                        )}
                                      </div>
                                    ) : <span className="small muted">{o.listing?.isCatalog ? 'Sem leitura' : '—'}</span>}
                                  </td>
                                  <td className="wrap">
                                    <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
                                      {o.alerts.filter((a) => a !== 'default_price' || o.priceSource === 'default').map((a) => <Tag key={a} tone={ALERT[a].tone}>{ALERT[a].label}</Tag>)}
                                    </div>
                                  </td>
                                </tr>
                              ))}
                        </tbody>
                      </table>
                    </div>
                    <div className="cards">
                      {rows.map((o) => (
                        <div key={o.productId} className="card-row" style={{ gridTemplateColumns: '1fr auto' }}>
                          <div style={{ minWidth: 0 }}>
                            <div className="t">{titleOf(o)}</div>
                            <div className="m">SKU {o.sku} · custo {brl(o.cost)} · margem {pct(o.marginPct)}</div>
                            <div style={{ marginTop: 4, display: 'flex', gap: 4, flexWrap: 'wrap' }}>
                              {o.priceSource && <Tag tone={SOURCE[o.priceSource].tone}>{SOURCE[o.priceSource].label}</Tag>}
                              {o.alerts.filter((a) => a !== 'default_price').map((a) => <Tag key={a} tone={ALERT[a].tone}>{ALERT[a].label}</Tag>)}
                            </div>
                          </div>
                          <div className="r">
                            <div><b>{brl(o.finalPrice)}</b></div>
                            <div className="muted">{o.finalStock ?? 0} un.</div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </>
                )}
              {list.data && <Pager total={list.data.total} page={page} pageSize={list.data.pageSize} onPage={(p) => set({ page: p > 1 ? String(p) : null })} />}
            </div>
          </div>
        </div>
      )}
      {account && <EditPrice offer={editing} accountId={account.id} costs={costs} onClose={() => setEditing(null)} />}
    </>
  );
}

/** Preço na mão: mostra a margem enquanto digita e permite voltar para o automático. */
function EditPrice({ offer, accountId, costs, onClose }: { offer: OfferRow | null; accountId: string; costs: ChannelCosts | null; onClose: () => void }) {
  const qc = useQueryClient();
  const [value, setValue] = useState('');
  const [stock, setStock] = useState('');
  const save = useMutation({
    mutationFn: (body: { fixedPrice?: number | null; stockOverride?: number | null }) =>
      api(`/channels/accounts/${accountId}/offers/${offer!.productId}`, { method: 'PUT', json: body }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['meli'] });
      onClose();
    },
  });
  const price = Number(value.replace(',', '.'));
  const cost = offer?.cost ? Number(offer.cost) : null;
  const ev = costs && cost && price > 0 ? evaluatePrice(price, cost, costs) : null;

  return (
    <Dialog.Root open={!!offer} onOpenChange={(o) => { if (!o) onClose(); else { setValue(offer?.fixedPrice ?? offer?.finalPrice ?? ''); setStock(offer?.stockOverride?.toString() ?? ''); } }}>
      <Dialog.Portal>
        <Dialog.Overlay className="drawer-overlay" />
        <Dialog.Content
          className="panel"
          style={{ position: 'fixed', top: '14vh', left: '50%', transform: 'translateX(-50%)', width: 'min(460px, calc(100vw - 32px))', zIndex: 60, display: 'grid', gap: 14 }}
          onOpenAutoFocus={() => { setValue(offer?.fixedPrice ?? offer?.finalPrice ?? ''); setStock(offer?.stockOverride?.toString() ?? ''); }}
        >
          <div style={{ display: 'flex', alignItems: 'center' }}>
            <Dialog.Title asChild><h2 style={{ margin: 0 }}>Preço na mão</h2></Dialog.Title>
            <span style={{ flex: 1 }} />
            <Dialog.Close className="icon-btn" aria-label="Fechar"><X size={18} /></Dialog.Close>
          </div>
          <Dialog.Description className="small muted" style={{ margin: 0 }}>
            {offer && <>SKU {offer.sku} · {titleOf(offer)} · custo {brl(offer.cost)} · PMC {brl(offer.pmc)} · calculado {brl(offer.computedPrice)}</>}
          </Dialog.Description>
          <div className="field">
            <label>Preço (R$)</label>
            <input className="input" inputMode="decimal" autoFocus value={value} onChange={(e) => setValue(e.target.value)} />
            <span className="small" style={{ color: ev && costs && ev.marginPct < costs.minMarginAlertPct ? 'var(--err)' : 'var(--muted)' }}>
              {ev ? <>Margem {ev.marginPct.toLocaleString('pt-BR')}% · lucro {brl(ev.profit)} · taxas {brl(ev.fees)}</> : 'Digite o preço para ver a margem.'}
              {offer?.pmc && price > Number(offer.pmc) && ' · acima do PMC'}
            </span>
          </div>
          <div className="field">
            <label>Ajuste de estoque para o canal (un.)</label>
            <input className="input" inputMode="numeric" placeholder="Vazio = automático" value={stock} onChange={(e) => setStock(e.target.value)} />
          </div>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            <button className="btn primary" type="button" disabled={!(price > 0) || save.isPending}
              onClick={() => save.mutate({ fixedPrice: price, stockOverride: stock.trim() ? Number(stock) : null })}>
              Salvar
            </button>
            {(offer?.fixedPrice || offer?.stockOverride != null) && (
              <button className="btn" type="button" disabled={save.isPending} onClick={() => save.mutate({ fixedPrice: null, stockOverride: null })}>
                Voltar para o automático
              </button>
            )}
          </div>
          {save.error && <ErrorBox error={save.error} />}
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}
